import React, { useState, useEffect } from 'react';
import { Star, MessageSquare } from 'lucide-react';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const StarRating = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(star => (
      <Star key={star} className={`w-5 h-5 ${
        star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
      }`} />
    ))}
  </div>
);

export default function ReviewsPage() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

useEffect(() => {
  if (restaurant?.name) {
    document.title = `Reviews - ${restaurant.name}`;
  } else {
    document.title = 'Customer Reviews';
  }
}, [restaurant]);

  useEffect(() => {
    const load = async () => {
      try {
       const [reviewData, statsData, restData] = await Promise.all([
        api.getReviews(),
        api.getReviewsStats(),
        api.getRestaurant()
      ]);

      setReviews(reviewData);
      setRestaurant({ ...restData, reviewStats: statsData });
      } catch (err) {
        console.error('Failed to load reviews:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const avgRating = restaurant?.reviewStats?.avgRating || 0;
  const totalReviews = restaurant?.reviewStats?.count || 0;

  return (

    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="text-gray-600 hover:text-gray-900 text-sm font-semibold">
            ← Back to Menu
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Page Title & Overall Rating */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Customer Reviews</h1>
          <p className="text-gray-600 mb-6">See what our customers are saying</p>
          
          <div className="inline-flex flex-col items-center bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="text-5xl font-bold text-gray-900 mb-2">{avgRating.toFixed(1)}</div>
            <StarRating rating={Math.round(avgRating)} />
            <p className="text-gray-600 text-sm mt-2">
              Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
            </p>
          </div>
        </div>

        {/* Reviews Grid */}
        {reviews.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No reviews yet</h3>
            <p className="text-gray-500">Be the first to leave a review after your order!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reviews.map(review => (
              <div key={review.id} className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                    {review.customerName?.charAt(0).toUpperCase() || 'A'}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{review.customerName || 'Anonymous'}</p>
                    <StarRating rating={review.rating} />
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(review.createdAt || review.orderDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                {review.comment && (
                  <p className="text-sm text-gray-700 leading-relaxed">
                    "{review.comment}"
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer restaurant={restaurant} />
    </div>
  );
}
